import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, catchError, expand, map, of, reduce, take, tap } from 'rxjs';
import { Bundle, ChargeItemDefinition } from 'fhir/r4';
import { backendEndPointToken } from '../app.config';
import { StateService } from './state.service';

@Injectable({ providedIn: 'root' })
export class ChargeItemDefinitionSearchService {
    private readonly http = inject(HttpClient);
    private readonly backendUrl = inject(backendEndPointToken);
    private readonly stateService = inject(StateService);

    private readonly priceMap = new BehaviorSubject<Map<string, ChargeItemDefinition>>(new Map());
    readonly priceMap$ = this.priceMap.asObservable();

    private isLoading = false;
    private hasLoaded = false;

    /**
     * Lazy-loads all active ChargeItemDefinitions into memory.
     * Supports multi-page fetching using FHIR pagination links.
     */
    prefetch(): Observable<Map<string, ChargeItemDefinition>> {
        if (this.hasLoaded || this.isLoading) {
            return this.priceMap$.pipe(take(1));
        }

        this.isLoading = true;
        const initialUrl = `${this.backendUrl}/ChargeItemDefinition?status=active&_count=200`;
        const accumulatedMap = new Map<string, ChargeItemDefinition>();

        return this.fetchPage(initialUrl).pipe(
            expand((bundle) => {
                const nextLink = bundle.link?.find((l) => l.relation === 'next')?.url;
                return nextLink ? this.fetchPage(nextLink) : EMPTY;
            }),
            tap((bundle) => {
                const entries = bundle.entry || [];
                entries.forEach((entry) => {
                    const resource = entry.resource;
                    if (resource) {
                        const codes = this.extractAllCodes(resource);
                        codes.forEach((code) => {
                            accumulatedMap.set(code.toLowerCase(), resource);
                        });

                        // Fix: Also index by instance reference so medications without codes can be found by their ID
                        if (resource.instance?.[0]?.reference) {
                            accumulatedMap.set(resource.instance[0].reference, resource);
                        }
                    }
                });
                // Sync with StateService incrementally
                this.stateService.processOrgWideBundleTransaction(bundle as any);
            }),
            reduce((acc) => acc, null), // Wait for all pages to complete
            map(() => {
                this.priceMap.next(accumulatedMap);
                this.hasLoaded = true;
                this.isLoading = false;
                return accumulatedMap;
            }),
            catchError((err) => {
                console.error('Failed to prefetch ChargeItemDefinitions:', err);
                this.isLoading = false;
                if (accumulatedMap.size > 0) {
                    this.priceMap.next(accumulatedMap);
                    this.hasLoaded = true;
                }
                return of(this.priceMap.value);
            })
        );
    }

    private fetchPage(url: string): Observable<Bundle<ChargeItemDefinition>> {
        const finalUrl = url.startsWith('http') ? url : `${this.backendUrl}/${url.startsWith('/') ? url.slice(1) : url}`;
        return this.http.get<Bundle<ChargeItemDefinition>>(finalUrl);
    }

    /**
     * Re-fetches specific resources by ID to get the absolute fresh state.
     */
    refreshByIds(ids: string[]): Observable<ChargeItemDefinition[]> {
        if (!ids?.length) {
            return of([]);
        }

        const url = `${this.backendUrl}/ChargeItemDefinition?_id=${ids.join(',')}`;
        return this.http.get<Bundle<ChargeItemDefinition>>(url).pipe(
            map(bundle => (bundle.entry || []).map(e => e.resource).filter((r): r is ChargeItemDefinition => !!r)),
            tap(resources => {
                const currentMap = new Map(this.priceMap.value);
                resources.forEach(r => {
                    const codes = this.extractAllCodes(r);
                    codes.forEach(code => {
                        currentMap.set(code.toLowerCase(), r);
                    });
                    if (r.instance?.[0]?.reference) {
                        currentMap.set(r.instance[0].reference, r);
                    }
                });
                this.priceMap.next(currentMap);

                // Sync with StateService
                const bundle = {
                    resourceType: 'Bundle',
                    type: 'collection',
                    entry: resources.map(r => ({ resource: r }))
                };
                this.stateService.processOrgWideBundleTransaction(bundle as any);
            })
        );
    }

    getDefinitionByCode(code: string): ChargeItemDefinition | undefined {
        if (!code) return undefined;
        return this.priceMap.value.get(code.toLowerCase());
    }

    private extractAllCodes(resource: ChargeItemDefinition): string[] {
        const codes: string[] = [];
        if (resource.code?.coding) {
            resource.code.coding.forEach(c => {
                if (c.code) codes.push(c.code);
            });
        }
        if (resource.code?.text) {
            codes.push(resource.code.text);
        }
        return Array.from(new Set(codes)); // Unique codes
    }
}
